import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Users, Globe, Check } from 'lucide-react';
import DefaultCommunityImage from '../../images/default-community.png';
import IsAuth from '../../../createContext/is-Auth/IsAuthContext';
import ProfilePassionsGrid from '../../Profile/Components/ProfilePassionsGrid';

const CommunityShareModal = () => {
  const { grpId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useContext(IsAuth);
  
  const [community, setCommunity] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isJoined, setIsJoined] = useState(false);

  useEffect(() => {
    const fetchCommunityDetails = async () => {
      if (!grpId) {
        setError('No community ID provided');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_URL}/api/communities/${grpId}`,
          { 
            headers: {token}
          }
        );

        setCommunity(response.data);
        
        // Check if user has already joined
        const userHasJoined = response.data.members.some(
          member => member?.id === user?._id
        );
      } catch (err) {
        console.log(err);
        setError(err.response?.data?.message || 'Failed to fetch community details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommunityDetails();
  }, []);

  useEffect(()=>{
    const userHasJoined = community?.members.some(
        member => member?.id === user?._id
    );
    setIsJoined(userHasJoined);
  },[community,user])

  const handleJoinCommunity = async () => {
    if (isJoined) return;

    try {
      await axios.post(
        `${process.env.REACT_APP_CHAT_URL}/api/communities/join/${grpId}`,
        {},
        { 
          headers: {token}
        }
      );

      setIsJoined(true);
      // Optionally, you can redirect to the community page
      navigate('/messages');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to join community');
    }
  };

  const handleClose = () => {
    navigate('/messages');
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg">
          <p>Loading community details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg text-red-500">
          <p>{error}</p>
          <button 
            onClick={handleClose} 
            className="mt-4 bg-red-500 text-white px-4 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 h-full bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white mt-5 mb-5 w-full max-w-md mx-4 rounded-xl shadow-2xl relative overflow-auto">
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 z-10"
        >
          <X size={24} />
        </button>

        {/* Community Header */}
        <div className="bg-gray-100 p-6 text-gray-800">
          <div className="flex items-center space-x-4">
            <img 
              src={DefaultCommunityImage} 
              alt={community.name} 
              className="w-20 h-20 rounded-full border-4 border-white"
            />
            <div>
              <h2 className="text-2xl font-bold">{community.name}</h2>
              <div className="flex items-center space-x-2 text-sm">
                <Users size={16} />
                <span>{community.members.length} Members</span>
              </div>
            </div>
          </div>
        </div>

        {/* Community Details */}
        <div className="p-6 space-y-4">
          {/* Description */}
          <div>
            <h3 className="font-semibold text-lg mb-2">Description</h3>
            <p className="text-gray-600">{community.description}</p>
          </div>

          {/* Additional Details */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-sm text-gray-500">Industry</h4>
              <p className="flex items-center">
                <Globe size={16} className="mr-2 text-blue-500" />
                {community.industry || 'Not specified'}
              </p>
            </div>
            <div>
              <h4 className="font-medium text-sm text-gray-500">Location</h4>
              <p>{community.location || 'Worldwide'}</p>
            </div>
          </div>

          {/* Passions */}
          {community.passions && community.passions.length > 0 && (
            <div>
              <h3 className="font-semibold text-lg mb-2">Community Passions</h3>
              <ProfilePassionsGrid
                passions={community.passions} 
                showPassions={true} 
              />
            </div>
          )}

          {/* Admins */}
          <div>
            <h3 className="font-semibold text-lg mb-2">
              Admins ({community.admins.length})
            </h3>
            <div className="flex space-x-2">
              {community.admins.slice(0, 3).map(admin => (
                <img 
                  key={admin._id}
                  src={admin.avatar || DefaultCommunityImage} 
                  alt={admin.userName}
                  className="w-10 h-10 rounded-full border"
                  title={admin.userName}
                />
              ))}
              {community.admins.length > 3 && (
                <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-sm">
                  +{community.admins.length - 3}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Join/Joined Button */}
        <div className="p-6 border-t flex justify-between items-center">
          <button 
            onClick={handleJoinCommunity}
            disabled={isJoined}
            className={`w-full py-3 rounded-lg text-white font-semibold transition-all duration-300 ${
              isJoined 
                ? 'bg-green-500 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
            }`}
          >
            {isJoined ? (
              <div className="flex items-center justify-center">
                <Check size={20} className="mr-2" /> Joined
              </div>
            ) : (
              'Join Community'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunityShareModal;